import React from "react";

/**
 * The v2 section card: everything on these screens that groups content sits in one of these.
 *
 * The shell carries no padding of its own — headers, rows and notes manage their own insets — so
 * tables and edge-to-edge row lists can run flush to the border while text blocks indent.
 *
 * A column, so a CardNote can be pinned to the bottom. Cards side by side in a grid stretch to the
 * tallest of them; without this the shorter card's closing note floats mid-air with dead space
 * beneath it, and two cards that should read as a matched pair visibly disagree.
 */
export const Card: React.FC<{ className?: string; id?: string; children: React.ReactNode }> = ({ className = "", id, children }) => (
    <section
        id={id}
        className={`flex flex-col overflow-hidden rounded-lg border border-[var(--line)] bg-[var(--surface)] ${className}`}
    >
        {children}
    </section>
);

type CardHeadProps = {
    title: string;
    /** Muted inline annotation beside the title ("fleet-wide", "what the unit returns to…"). */
    note?: React.ReactNode;
    /** Uppercase tag on the far side of the header ("Within director limits"). */
    eyebrow?: string;
    /** Phone density: tighter padding and a slightly smaller title, matching cab row insets. */
    dense?: boolean;
    /** Right-aligned actions (buttons, tags). */
    children?: React.ReactNode;
};

export const CardHead: React.FC<CardHeadProps> = ({ title, note, eyebrow, dense, children }) => (
    // Wraps: on a phone a title, its note and an action pill cannot share one line, and without
    // this the title breaks mid-phrase to make room. The note drops under the title instead.
    <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 ${dense ? "px-4 pt-3.5" : "px-5 pt-4"}`}>
        <span className={`font-semibold tracking-tight ${dense ? "text-[14px]" : "text-[15px]"}`}>{title}</span>
        {note && <span className="min-w-0 text-[12px] text-[var(--muted)]">{note}</span>}
        <span className="ml-auto flex items-center gap-2">
            {eyebrow && (
                <span className="text-[10px] font-semibold uppercase tracking-[0.13em] text-[var(--faint)]">{eyebrow}</span>
            )}
            {children}
        </span>
    </div>
);

/**
 * Flat explanatory strip along the bottom of a Card — quieter than a nested Note.
 *
 * `mt-auto` is what keeps it *at* the bottom rather than merely last: in a row of cards the shell
 * stretches to the tallest, and a note that simply follows the content leaves a gap below itself.
 *
 * Its inset matches CardHead's, so the note lines up with the title above it — pass `dense`
 * wherever the head is dense.
 */
export const CardNote: React.FC<{ dense?: boolean; children: React.ReactNode }> = ({ dense, children }) => (
    <div
        className={`mt-auto border-t border-[var(--line)] bg-[var(--surface-2)] py-3 text-[12px] leading-relaxed text-[var(--muted)] ${
            dense ? "px-4" : "px-5"
        }`}
    >
        {children}
    </div>
);
